import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Gift, TrendingUp } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { gardienById, PRODUCT_GARDIENS } from "@/data/gardiens";

const filters = ["Tous", "Mémoire", "Apaisement", "Stimulation", "Repères"];

const DEFAULTS = {
  shop_eyebrow: "La boutique",
  shop_title: "Des objets prêts à offrir de la douceur",
  shop_subtitle: "Une sélection de créations fabriquées main, adaptées aux besoins des personnes fragilisées. Chaque pièce peut aussi être personnalisée sur demande.",
  shop_offer_label: "Offert dès 100 € d'achat (particuliers) :",
  shop_offer_text: "Accès illimité à notre application de jeux et d'accompagnement cognitif.",
};

const Boutique = () => {
  const [products, setProducts] = useState([]);
  const [active, setActive] = useState("Tous");
  const [c, setC] = useState(DEFAULTS);
  const { addToCart } = useApp();

  useEffect(() => { api.get("/products").then((r) => setProducts(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/content").then((r) => setC({ ...DEFAULTS, ...r.data })).catch(() => {}); }, []);
  const shown = active === "Tous" ? products : products.filter((p) => p.category === active);

  return (
    <div className="pt-32">
      <section className="max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal>
          <p className="text-sm uppercase tracking-[0.3em] text-primary mb-5">{c.shop_eyebrow}</p>
          <h1 className="font-serif-display text-5xl sm:text-6xl font-light max-w-3xl leading-tight">{c.shop_title}</h1>
          <p className="mt-6 text-lg text-foreground/70 max-w-2xl leading-relaxed">
            {c.shop_subtitle}
          </p>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="mt-12 rounded-[2.5rem] overflow-hidden border border-warm/25 shadow-[0_20px_60px_rgb(0,0,0,0.08)] grain" data-testid="shop-banner">
            <img src="/affiche-boutique.jpg" alt="L'atelier Passerelle — bougie, cœur en résine, boîte à souvenirs et plaid faits main, veillés par une gardienne" className="w-full h-auto block" />
          </div>
        </Reveal>

        <div className="flex flex-wrap gap-3 mt-12" data-testid="category-filters">
          {filters.map((f) => (
            <button
              key={f}
              data-testid={`filter-${f.toLowerCase()}`}
              onClick={() => setActive(f)}
              className={`rounded-full px-6 py-2.5 text-sm border transition-colors ${active === f ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-accent"}`}
            >
              {f}
            </button>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
        <Reveal>
          <div data-testid="app-offer-banner" className="mb-12 rounded-2xl bg-warm/15 border border-warm/40 px-6 py-5 flex items-center gap-4">
            <span className="shrink-0 w-11 h-11 rounded-full bg-warm/25 flex items-center justify-center">
              <Gift className="w-5 h-5 text-warm" strokeWidth={1.8} />
            </span>
            <p className="text-sm sm:text-base text-foreground/80">
              <strong className="text-foreground">{c.shop_offer_label}</strong> {c.shop_offer_text}
            </p>
          </div>
        </Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {shown.map((p, i) => (
            <Reveal key={p.slug} delay={(i % 3) * 0.08}>
              <article data-testid={`product-card-${p.slug}`} className="group bg-card rounded-3xl border border-border/50 overflow-hidden hover:-translate-y-1 hover:shadow-[0_20px_40px_rgb(0,0,0,0.08)] transition-all duration-500 h-full flex flex-col">
                <div className="relative overflow-hidden aspect-[4/3]">
                  <img src={p.image} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  <span className="absolute top-4 left-4 bg-background/85 backdrop-blur text-xs px-3 py-1.5 rounded-full">{p.condition}</span>
                </div>
                <div className="p-7 flex flex-col flex-1">
                  <p className="text-xs uppercase tracking-[0.2em] text-secondary-foreground/60">{p.category}</p>
                  <h3 className="font-serif-display text-2xl mt-2">{p.name}</h3>
                  <p className="mt-3 text-sm text-foreground/60 leading-relaxed flex-1">{p.description}</p>
                  <div className="mt-4 flex items-center gap-2 text-xs text-warm">
                    <Gift className="w-3.5 h-3.5 shrink-0" strokeWidth={2} />
                    <span>Application offerte dès 100 € d'achat</span>
                  </div>
                  <p className="mt-1.5 flex items-center gap-2 text-xs text-foreground/55" data-testid={`product-palier-${p.slug}`}>
                    <TrendingUp className="w-3.5 h-3.5 shrink-0" strokeWidth={2} />
                    <span>Cet article vous rapproche de l'accès offert</span>
                  </p>
                  {(() => { const gid = p.gardien || PRODUCT_GARDIENS[p.slug]; const g = gid && gardienById(gid); return g && (
                    <Link to={`/gardiens/${g.id}`} data-testid={`product-gardien-${p.slug}`} className="mt-3 flex items-center gap-2.5 text-xs text-foreground/70 hover:text-primary transition-colors group/g">
                      <img src={g.image} alt={g.name} className="w-8 h-8 rounded-full object-cover object-top shrink-0 group-hover/g:scale-110 transition-transform" style={{ boxShadow: `0 0 0 2px ${g.color}55` }} />
                      <span>Son Gardien fétiche : <strong className="font-medium">{g.name}</strong> · {g.value}</span>
                    </Link>
                  ); })()}
                  <div className="flex items-center justify-between mt-6">
                    <span className="font-serif-display text-3xl">{p.price.toFixed(0)} €</span>
                    <button
                      data-testid={`add-to-cart-${p.slug}`}
                      onClick={() => addToCart(p)}
                      className="rounded-full bg-primary text-primary-foreground w-12 h-12 flex items-center justify-center hover:scale-110 transition-transform duration-300"
                      aria-label={`Ajouter ${p.name}`}
                    >
                      <Plus className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Boutique;
